-- | Work-queue rendering and local UI events.
-- |
-- | Rendering stays here so `State` and `Cache` can be imported without pulling
-- | DOM code into reducer/runtime modules.
module Steps.WorkQueue.View
    ( WorkQueueResult(..)
    , workQueueWidget
    , workQueueWidgetWithRoute
    , loadingWidgetWQ
    ) where

import Common

import API.FormBase (FormBasic, FormID)
import API.FormType (FormSection, FormType)
import API.RecentForms (RecentForm)
import API.SelStudent (SelStudent, studentName)
import App.Route as AppRoute
import Concur.React.DOM as D
import Concur.React.Props as P
import Control.Comonad.Cofree as Cofree
import Data.Array (filter)
import Data.Array as Arr
import Data.List as List
import Data.Map as Map
import Data.Maybe (isNothing, maybe)
import Data.Tree (Tree)
import GraphQL.Type (innerId)
import Internal.ConcurUtil (loopInfinitely)
import Internal.SelectionMap as Sel
import React.SyntheticEvent (SyntheticMouseEvent)
import Steps.WorkQueue.Cache (WorkQueueCache, getDocByStudent, isLockedDocument)
import Steps.WorkQueue.DocumentIndex (DocumentLookup, listDocs)
import Steps.WorkQueue.State (WorkQueueNavigation, WorkQueueState, isWorkQueueEmpty)
import Steps.WorkQueue.UIState (ExpandedState(..), WorkQueueUiState)
import Widgets.Loading (loadingWidget)

-- | Result of something the user did in the work queue.
data WorkQueueResult
    = UIStateChange WorkQueueUiState
    -- ^ View-only state change.
    | JumpToForm WorkQueueNavigation SyntheticMouseEvent
    -- ^ Jump to a section in the given document.

workQueueWidget :: WorkQueueState -> WorkQueueUiState -> WorkQueueCache -> Widget HTML WorkQueueResult
workQueueWidget = workQueueWidgetWithRoute $ AppRoute.preserveQuery ""

workQueueWidgetWithRoute
    :: AppRoute.PreservedQuery
    -> WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> Widget HTML WorkQueueResult
workQueueWidgetWithRoute preservedQuery state ui cache = D.div [ P.className "workqueue" ] $
    [ renderStudentList preservedQuery state ui cache
    ] <> if isWorkQueueEmpty state then [] else [ expandBtns ]
  where
    expandBtns :: Widget HTML WorkQueueResult
    expandBtns = D.div
        [ P.className "expand-collapse-buttons" ]
        [ D.button
            [ UIStateChange ui { expandedStudents = expansionForAll Expanded } <$ P.onClick
            , P.className "expand-all"
            ]
            [ D.text "Expand all" ]
        , D.button
            [ UIStateChange ui { expandedStudents = expansionForAll Collapsed } <$ P.onClick
            , P.className "collapse-all"
            ]
            [ D.text "Collapse all" ]
        ]

    expansionForAll expansion =
        Map.fromFoldable $
            map (\student -> Tuple student.id expansion) $
                Sel.listSelected state.students

renderStudentList
    :: AppRoute.PreservedQuery
    -> WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> Widget HTML WorkQueueResult
renderStudentList preservedQuery state ui cache = D.div [ P.className "workqueue-students" ] $
    map (renderStudentDetails preservedQuery state ui cache) $ Arr.fromFoldable $ Sel.listSelected state.students

renderStudentDetails
    :: AppRoute.PreservedQuery
    -> WorkQueueState
    -> WorkQueueUiState
    -> WorkQueueCache
    -> SelStudent
    -> Widget HTML WorkQueueResult
renderStudentDetails preservedQuery state ui cache student =
    if length studentForms <= 0
        then renderStudentNoForms
        else renderStudentAndForms
  where
    renderStudentNoForms = D.li
        [ P.className "workqueue-student no-forms" ]
        [ D.text $ studentName cache.account student ]

    renderStudentAndForms = D.li
        [ P.className "workqueue-student" ]
        [ D.details
            ([ P.key $ "student-details-" <> innerId student.id <> "-" <> if isOpen then "open" else "closed" ] <> openProp isOpen)
            [ D.summary
                [ UIStateChange ui { expandedStudents = toggleExpanded } <$ P.onClick
                , P.className "summary-student"
                ]
                [ D.text $ studentName cache.account student ]
            , renderStudentForms
            ]
        ]

    renderStudentForms = D.ul [ P.className "workqueue-student-forms" ] $
        map renderStudentForm studentForms

    studentForms = existingDocs <> newForms
      where
        existingDocs = map ExistingDocument $
            filter (\d -> d.student.id == student.id) state.editDocs
        newForms = map NewDocument $ Arr.fromFoldable $ Sel.listSelected state.forms

    toggleExpanded = Map.insert student.id
        (if isOpen then Collapsed else Expanded)
        ui.expandedStudents

    isOpen = case Map.lookup student.id ui.expandedStudents of
        Just Expanded -> true
        Just Collapsed -> false
        Nothing -> maybe false (\c -> c.studentId == student.id) state.currentDoc

    openProp true = [ P.open true ]
    openProp false = []

    renderStudentForm :: WorkQueueFormItem -> Widget HTML WorkQueueResult
    renderStudentForm item = D.li liAttrs
        [ case cachedFormMaybe of
            Just f ->
                if length f.sections > 0
                    then D.details [ P.open isCurrent ]
                        [ D.summary [ P.className "summary-form summary-form-sections" ]
                            [ formAnchor true ]
                        , renderSections f
                        ]
                    else formAnchor true
            Nothing -> formAnchor true
        ]
      where
        lookup = itemLookup student item
        isCurrent = isCurrentWorkQueueItem lookup
        isLocked = isLockedDocument state lookup
        cachedFormMaybe = Map.lookup (itemFormId item) cache.formCache

        isCurrentWorkQueueItem :: DocumentLookup -> Boolean
        isCurrentWorkQueueItem candidate = case state.currentDoc of
            Nothing -> false
            Just current ->
                if current == candidate then true
                else case current.documentId of
                    Just currentDocumentId ->
                        isNothing candidate.documentId
                            && not (currentDocumentId `Arr.elem` map _.id state.editDocs)
                            && maybe false
                                (\doc -> doc.student.id == candidate.studentId && doc.formId == candidate.formId)
                                (getDocByStudent state current)
                    Nothing -> false

        liAttrs =
            [ P.key $ itemKey student item
            ]
                <> if isNothing cachedFormMaybe
                    then [ P.className "workqueue-form-no-sections" ]
                    else []

        formAnchor handleJump = case item of
            ExistingDocument recentForm ->
                D.a
                    ( [ P.key $ itemKey student item <> ":" <> if isLocked then "locked" else "unlocked"
                      , P.className $ "workqueue-form-link" <> if isCurrent then " workqueue-selected" else ""
                      , P.href $ existingDocumentHref recentForm
                      ]
                        <> if handleJump
                            then
                                [ JumpToForm { lookup, section: Nothing } <$> P.onClick
                                ]
                            else []
                    )
                    $ renderFormLabel (itemLabel item) isLocked

            NewDocument _ ->
                D.button
                    ( [ P.key $ itemKey student item <> ":" <> if isLocked then "locked" else "unlocked"
                      , P.className $ "workqueue-form-link" <> if isCurrent then " workqueue-selected" else ""
                      , P._type "button"
                      ]
                        <> if handleJump
                            then
                                [ JumpToForm { lookup, section: Nothing } <$> P.onClick
                                ]
                            else []
                    )
                    $ renderFormLabel (itemLabel item) isLocked

        renderFormLabel :: String -> Boolean -> Array (Widget HTML WorkQueueResult)
        renderFormLabel label locked =
            [ D.span [ P.className "workqueue-form-label" ] $
                (if locked then [ D.span [ P.className "workqueue-lock-indicator" ] [] ] else [])
                <> [ D.span' [ D.text label ] ]
            ]

        renderSections :: FormType -> Widget HTML WorkQueueResult
        renderSections form' = D.ul [ P.className "student-form-section" ]
            $ map renderSection form'.sections

        renderSection :: Tree FormSection -> Widget HTML WorkQueueResult
        renderSection tree =
            let node = Cofree.head tree
            in D.li'
                [ D.a
                    [ JumpToForm { lookup, section: Just node.id } <$> P.onClick
                    , P.href $ sectionHref item node.id
                    ]
                    [ D.text node.workQueueLabel ]
                , D.ul' $ map renderSection $ List.toUnfoldable $ Cofree.tail tree
                ]

        existingDocumentHref :: RecentForm -> String
        existingDocumentHref recentForm = AppRoute.routeUrl $ AppRoute.RStep3
            { preservedQuery
            , activeDoc: Just recentForm.id
            , docs: routeDocIds
            , section: AppRoute.SectionClear
            }

        sectionHref :: WorkQueueFormItem -> String -> String
        sectionHref currentItem sectionId = case currentItem of
            ExistingDocument recentForm ->
                AppRoute.routeUrl $ AppRoute.RStep3
                    { preservedQuery
                    , activeDoc: Just recentForm.id
                    , docs: routeDocIds
                    , section: AppRoute.SectionSet sectionId
                    }
            NewDocument _ ->
                AppRoute.sectionHash $ Just sectionId

        routeDocIds =
            Arr.nub $
                (map _.id state.editDocs)
                    <> (map _.id $ Arr.fromFoldable $ listDocs state.documents)

data WorkQueueFormItem
    = ExistingDocument RecentForm
    | NewDocument FormBasic

itemLookup :: SelStudent -> WorkQueueFormItem -> DocumentLookup
itemLookup student = case _ of
    ExistingDocument recentForm ->
        { studentId: recentForm.student.id
        , formId: recentForm.form_type.id
        , documentId: Just recentForm.id
        }
    NewDocument form ->
        { studentId: student.id
        , formId: form.id
        , documentId: Nothing
        }

itemFormId :: WorkQueueFormItem -> FormID
itemFormId = case _ of
    ExistingDocument recentForm -> recentForm.form_type.id
    NewDocument form -> form.id

itemLabel :: WorkQueueFormItem -> String
itemLabel = case _ of
    ExistingDocument recentForm -> recentForm.form_type.label
    NewDocument form -> form.label

itemKey :: SelStudent -> WorkQueueFormItem -> String
itemKey student = case _ of
    ExistingDocument recentForm -> "existing:" <> innerId recentForm.id
    NewDocument form -> "new:" <> innerId student.id <> ":" <> innerId form.id

-- | A simple widget that displays a loading animation.
loadingWidgetWQ :: forall a. WorkQueueState -> WorkQueueUiState -> WorkQueueCache -> Widget HTML a
loadingWidgetWQ state ui cache = D.div [ P.className "step-container" ]
    [ D.div
        [ P.className "page-header" ]
        [ D.header
            [ P.className "page-heading" ]
            [ D.text $ lookupTranslation "loading" ]
        ]
    , loopInfinitely $ workQueueWidget state ui cache
    , loadingWidget
    ]
